import React, { useState } from "react";
import AvatarPreview from "./AvatarPreview";
import { shopItems } from "../data/shopItems";
import { trackAvatarCustomized } from "../lib/analytics";
import { FOOD_ITEMS, applyBoost, getStatColor, getStatLabel, defaultAvatarStats } from "../lib/tamagotchiUtils";

const AvatarCustomizer = ({ userData, onSave, onBack }) => {
  const [avatar, setAvatar] = useState(userData.avatar);
  const [username, setUsername] = useState(userData.username || "");
  const [equipped, setEquipped] = useState(userData.equippedItems);
  const [activeCategory, setActiveCategory] = useState("care");

  // Tamagotchi visual feedback
  const [displayStats, setDisplayStats] = useState(userData.avatarStats || defaultAvatarStats());
  const [feedFeedback, setFeedFeedback] = useState(null); // { emoji, label, key }
  const [tempMood, setTempMood] = useState(null);         // briefly overrides expression
  const [bouncing, setBouncing] = useState(false);

  const categories = [
    { id: "care", name: "Care 🐾" },
    { id: "environment", name: "Background" },
    { id: "skinColor", name: "Skin Color" },
    { id: "hair", name: "Hair" },
    { id: "hat", name: "Hats" },
    { id: "glasses", name: "Glasses" },
    { id: "eyebrows", name: "Eyebrows" },
    { id: "mouth", name: "Mouth" },
    { id: "beard", name: "Beard" },
    { id: "top", name: "Top" },
    { id: "bottom", name: "Bottom" },
  ];

  const feedAvatar = (food) => {
    if (userData.coins < food.cost) return;
    const newStats = applyBoost(displayStats, food.effects);

    // Immediately animate the stat bars
    setDisplayStats(newStats);

    // Build a short summary of what changed, e.g. "+30 Fullness · +5 Mood"
    const gained = Object.entries(food.effects)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `+${v} ${k.charAt(0).toUpperCase() + k.slice(1)}`)
      .join(' · ');
    setFeedFeedback({ emoji: food.emoji, label: gained, key: Date.now() });
    setTimeout(() => setFeedFeedback(null), 1600);

    // Flash avatar to happy expression
    setTempMood({ eyebrows: 'happy', mouth: 'teeth' });
    setTimeout(() => setTempMood(null), 2000);

    // Bounce the avatar
    setBouncing(true);
    setTimeout(() => setBouncing(false), 600);

    // Persist to DB
    onSave({ ...userData, coins: userData.coins - food.cost, avatarStats: newStats });
  };

  const buyItem = (item) => {
    if (
      userData.coins >= item.cost &&
      !userData.unlockedItems.includes(item.id)
    ) {
      const newData = {
        ...userData,
        coins: userData.coins - item.cost,
        unlockedItems: [...userData.unlockedItems, item.id],
      };
      onSave(newData);
      alert("Unlocked " + item.name + "!");
    }
  };

  const toggleEquip = (item) => {
    const newEquipped = { ...equipped };

    if (item.category === "skinColor") {
      newEquipped.skinColor = item.id; // ← CHANGE from item.color to item.id
      setAvatar({ ...avatar, skinColor: item.id }); // ← CHANGE from item.color to item.id
    } else if (item.category === "environment") {
      newEquipped.environment = item.id;
      setAvatar({ ...avatar, environment: item.id });
    } else {
      // For other categories, toggle on/off (allow null for optional items)
      if (equipped[item.category] === item.id) {
        newEquipped[item.category] = null;
        setAvatar({ ...avatar, [item.category]: null });
      } else {
        newEquipped[item.category] = item.id;
        setAvatar({ ...avatar, [item.category]: item.id });
      }
    }

    setEquipped(newEquipped);
  };

  const handleSave = () => {
    const updatedData = {
      ...userData,
      avatar,
      username: username.trim() || userData.username,
      equippedItems: equipped,
    };
    onSave(updatedData);
    // Track avatar customization in Google Analytics
    trackAvatarCustomized(equipped);
    onBack();
  };

  const categoryItems = shopItems.filter(
    (item) => item.category === activeCategory
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <style>{`
        @keyframes floatUp {
          0%   { opacity: 1; transform: translateY(0) scale(1); }
          20%  { opacity: 1; transform: translateY(-8px) scale(1.05); }
          100% { opacity: 0; transform: translateY(-48px) scale(0.9); }
        }
        @keyframes avatarBounce {
          0%,100% { transform: scale(1); }
          30%     { transform: scale(1.12) rotate(-3deg); }
          60%     { transform: scale(0.96) rotate(2deg); }
          80%     { transform: scale(1.04); }
        }
        .feed-float  { animation: floatUp 1.4s ease-out forwards; }
        .avatar-bounce { animation: avatarBounce 0.55s ease-in-out; }
      `}</style>

      <button
        onClick={onBack}
        className="mb-4 text-white hover:text-gray-200"
      >
        ← Back to Dashboard
      </button>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Avatar Preview */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Your Avatar</h2>
          <div className="flex justify-center mb-6 relative">
            <div className={bouncing ? 'avatar-bounce' : ''}>
              <AvatarPreview avatar={avatar} size="lg" moodOverride={tempMood} />
            </div>
            {/* Floating feed feedback */}
            {feedFeedback && (
              <div
                key={feedFeedback.key}
                className="feed-float absolute -top-2 left-1/2 -translate-x-1/2 pointer-events-none
                           bg-white border border-green-300 text-green-700 text-xs font-bold
                           rounded-full px-3 py-1 shadow-md whitespace-nowrap z-10"
              >
                {feedFeedback.emoji} {feedFeedback.label}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={userData.username || "Choose a username"}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                maxLength={20}
              />
            </div>
          </div>

          <button
            onClick={handleSave}
            className="w-full mt-6 bg-teal-500 text-white py-3 rounded-lg font-semibold hover:bg-teal-600"
          >
            Save Avatar & Username
          </button>
        </div>

        {/* Category Tabs */}
        <div className="md:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Customize</h2>
          <div className="text-lg font-semibold text-yellow-600 mb-6">
            Your Coins: {userData.coins}
          </div>

          {/* Category Navigation */}
          <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeCategory === cat.id
                    ? "bg-teal-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Care Tab */}
          {activeCategory === "care" && (
            <div className="space-y-4">
              {/* Stat Bars */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-700">Avatar Stats</h3>
                {[
                  { label: '⚡ Energy',   key: 'energy' },
                  { label: '😊 Mood',     key: 'happiness' },
                  { label: '🍎 Fullness', key: 'fullness' },
                ].map(({ label, key }) => {
                  const value = Math.round(displayStats?.[key] ?? 80);
                  const lbl = getStatLabel(value);
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>{label}</span>
                        <span className={lbl ? (value < 30 ? 'text-red-500 font-bold' : 'text-yellow-500 font-bold') : 'text-gray-500'}>
                          {value}% {lbl && `— ${lbl}`}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-700 ${getStatColor(value)}`}
                          style={{ width: `${value}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Food Items */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Feed your avatar</h3>
                <div className="grid grid-cols-1 gap-2">
                  {FOOD_ITEMS.map((food) => {
                    const canAfford = userData.coins >= food.cost;
                    return (
                      <div key={food.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{food.emoji}</span>
                          <div>
                            <div className="font-medium text-gray-800 text-sm">{food.name}</div>
                            <div className="text-xs text-gray-500">{food.description}</div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {Object.entries(food.effects)
                                .filter(([, v]) => v > 0)
                                .map(([k, v]) => `+${v} ${k}`)
                                .join(' · ')}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => feedAvatar(food)}
                          disabled={!canAfford}
                          className={`ml-3 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
                            canAfford
                              ? 'bg-yellow-400 text-white hover:bg-yellow-500'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          🪙 {food.cost}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Items Grid */}
          {activeCategory !== "care" && (
          <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
            {categoryItems.map((item) => {
              const unlocked =
                userData.unlockedItems.includes(item.id) || item.cost === 0;
              const canAfford = userData.coins >= item.cost;
              const isEquipped =
                equipped[item.category] === item.id ||
                (item.category === "skinColor" &&
                  equipped.skinColor === item.color);
              const ItemIcon = item.icon;

              return (
                <div
                  key={item.id}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    isEquipped
                      ? "border-teal-500 bg-teal-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {item.category === "skinColor" ? (
                      <img
                        src={`/avatar-base-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded-full border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "environment" ? (
                      <img
                        src={`/avatar-env-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "hair" ? (
                      <img
                        src={`/avatar-hair-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded-full border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "hat" ? (
                      <img
                        src={`/avatar-hat-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "glasses" ? (
                      <img
                        src={`/avatar-glasses-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "eyebrows" ? (
                      <img
                        src={`/avatar-eyebrows-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "mouth" ? (
                      <img
                        src={`/avatar-mouth-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "beard" ? (
                      <img
                        src={`/avatar-beard-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "top" ? (
                      <img
                        src={`/avatar-top-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded border-2 border-gray-300 object-cover"
                      />
                    ) : item.category === "bottom" ? (
                      <img
                        src={`/avatar-bottom-${item.id}.png`}
                        alt={item.name}
                        className="w-8 h-8 rounded border-2 border-gray-300 object-cover"
                      />
                    ) : (
                      <ItemIcon className="w-6 h-6 text-gray-600" />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-gray-800 text-sm">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-600">
                        {item.cost === 0 ? "Free" : item.cost + " coins"}
                      </div>
                    </div>
                  </div>

                  {!unlocked ? (
                    <button
                      onClick={() => buyItem(item)}
                      disabled={!canAfford}
                      className={`w-full px-3 py-2 rounded-lg text-sm font-medium ${
                        canAfford
                          ? "bg-yellow-500 text-white hover:bg-yellow-600"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Buy
                    </button>
                  ) : (
                    <button
                      onClick={() => toggleEquip(item)}
                      className={`w-full px-3 py-2 rounded-lg text-sm font-medium ${
                        isEquipped
                          ? "bg-green-500 text-white"
                          : "bg-teal-500 text-white hover:bg-teal-600"
                      }`}
                    >
                      {isEquipped ? "✓ Equipped" : "Equip"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvatarCustomizer;
